import React, { useState, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableFooter, TableSortLabel, Paper, Tooltip,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile } from '@fortawesome/free-regular-svg-icons';
import NameCell from './patientTrackerV2/NameCell';

/**
 * EncounterTrackerTableV2
 * ----------------------------------------------------------------------------
 * Advanced MUI replacement for EncounterTrackerTable used by the v2 trackers.
 *  - MUI Table: sticky header, sortable columns (TableSortLabel), hover, zebra
 *  - renders a real <table>, so the page's innerHTML-based Excel/PDF export of
 *    the encounter table keeps working unchanged
 *  - identical click-to-open-modal wiring (handleGetPatientTrackerDetailedData
 *    with the same countType codes, and handleGetUserAudits)
 *
 * Drop-in: accepts the same props the v1 table received (extra props ignored).
 */

const COLUMNS = [
  { key: 'index', header: '#', sortable: false, type: 'index', align: 'center' },
  { key: 'ProviderName', header: 'Name', sortable: true, type: 'name', align: 'left' },
  { key: 'nonduplicatePatcount', header: 'Total Patients', sortable: true, type: 'link', countType: 2, align: 'center' },
  { key: 'SignedEnc', header: 'Signed Enc.', sortable: true, type: 'link', countType: 1, align: 'center' },
  { key: 'UnsignedEnc', header: 'Unsigned Enc.', sortable: true, type: 'link', countType: 0, align: 'center' },
  { key: 'totalEncounters', header: 'Total Enc.', sortable: true, type: 'totalEnc', countType: 3, align: 'center' },
  { key: 'CPTCount', header: 'Total CPTs', sortable: true, type: 'num', align: 'center' },
  { key: 'UnitCount', header: 'Total Units', sortable: true, type: 'num', align: 'center' },
];

const num = (v) => Number(v) || 0;
const rowTotalEnc = (r) => num(r.SignedEnc) + num(r.UnsignedEnc);

const EncounterTrackerTableV2 = ({ data = [], handleGetPatientTrackerDetailedData, handleGetUserAudits, onRowOpen }) => {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: null, asc: true });
  const [activeRow, setActiveRow] = useState(null);

  const filtered = useMemo(
    () => (data || []).filter((r) => (r.ProviderName || '').toLowerCase().includes(search.toLowerCase())),
    [data, search]
  );

  const sorted = useMemo(() => {
    if (!sort.key) return filtered;
    const arr = [...filtered];
    arr.sort((a, b) => {
      if (sort.key === 'ProviderName') {
        const va = (a.ProviderName || '').toLowerCase();
        const vb = (b.ProviderName || '').toLowerCase();
        return sort.asc ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      const va = sort.key === 'totalEncounters' ? rowTotalEnc(a) : num(a[sort.key]);
      const vb = sort.key === 'totalEncounters' ? rowTotalEnc(b) : num(b[sort.key]);
      return sort.asc ? va - vb : vb - va;
    });
    return arr;
  }, [filtered, sort]);

  const totals = useMemo(() => {
    const t = { nonduplicatePatcount: 0, SignedEnc: 0, UnsignedEnc: 0, totalEncounters: 0, CPTCount: 0, UnitCount: 0 };
    filtered.forEach((r) => {
      t.nonduplicatePatcount += num(r.nonduplicatePatcount);
      t.SignedEnc += num(r.SignedEnc);
      t.UnsignedEnc += num(r.UnsignedEnc);
      t.totalEncounters += rowTotalEnc(r);
      t.CPTCount += num(r.CPTCount);
      t.UnitCount += num(r.UnitCount);
    });
    return t;
  }, [filtered]);

  const toggleSort = (key) => setSort((p) => ({ key, asc: p.key === key ? !p.asc : true }));

  const openModal = (row, countType) => {
    if (handleGetPatientTrackerDetailedData) handleGetPatientTrackerDetailedData(row, countType);
  };

  const renderCell = (col, row) => {
    switch (col.type) {
      case 'name':
        return (
          <div className="ptv2-mt-name">
            <NameCell
              name={row.ProviderName}
              openable={!!onRowOpen}
              onClick={(e) => { if (onRowOpen) { e.stopPropagation(); onRowOpen(row); } }}
            />
            <Tooltip title="View audit" arrow>
              <span>
                <FontAwesomeIcon
                  icon={faFile}
                  className="ptv2-mt-audit"
                  onClick={(e) => { e.stopPropagation(); handleGetUserAudits && handleGetUserAudits(row); }}
                />
              </span>
            </Tooltip>
          </div>
        );
      case 'link':
        return <button type="button" className="ptv2-mt-link" onClick={() => openModal(row, col.countType)}>{row[col.key] || '0'}</button>;
      case 'totalEnc':
        return <button type="button" className="ptv2-mt-link" onClick={() => openModal(row, col.countType)}>{rowTotalEnc(row)}</button>;
      case 'num':
        return <span>{row[col.key] || '0'}</span>;
      default:
        return null;
    }
  };

  return (
    // No maxHeight. Capping it at 540 gave the card its own vertical scrollbar
    // on top of the page's, so the tracker had two bars scrolling the same
    // rows. TableContainer keeps its built-in overflow-x, which is what stops a
    // wide table dragging the page sideways.
    <TableContainer component={Paper} elevation={0} className="ptv2-mui-table">
      {/* stickyHeader kept only for its border-collapse:separate, which the
          header/footer borders in PatientTrackerV2.css are drawn against. */}
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {COLUMNS.map((col) => (
              <TableCell key={col.key} align={col.align} className="ptv2-mui-th">
                {col.sortable ? (
                  <TableSortLabel
                    active={sort.key === col.key}
                    direction={sort.key === col.key && !sort.asc ? 'desc' : 'asc'}
                    onClick={() => toggleSort(col.key)}
                  >
                    {col.header}
                  </TableSortLabel>
                ) : (
                  col.header
                )}
                {col.type === 'name' && (
                  <input
                    className="ptv2-mt-search"
                    type="text"
                    placeholder="Search name"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {sorted.length === 0 ? (
            <TableRow>
              <TableCell colSpan={COLUMNS.length} align="center" className="ptv2-mt-empty">No Data Available</TableCell>
            </TableRow>
          ) : (
            sorted.map((row, index) => (
              <TableRow
                key={index}
                hover
                selected={activeRow === index}
                onClick={() => setActiveRow(index)}
                className="ptv2-mui-row"
              >
                <TableCell align="center">{index + 1}</TableCell>
                {COLUMNS.slice(1).map((col) => (
                  <TableCell key={col.key} align={col.align}>{renderCell(col, row)}</TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>

        <TableFooter>
          <TableRow className="ptv2-mui-foot">
            <TableCell align="center">Σ</TableCell>
            <TableCell align="left">Total</TableCell>
            <TableCell align="center">{totals.nonduplicatePatcount}</TableCell>
            <TableCell align="center">{totals.SignedEnc}</TableCell>
            <TableCell align="center">{totals.UnsignedEnc}</TableCell>
            <TableCell align="center">{totals.totalEncounters}</TableCell>
            <TableCell align="center">{totals.CPTCount}</TableCell>
            <TableCell align="center">{totals.UnitCount}</TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

export default EncounterTrackerTableV2;
